import dayjs from 'dayjs'
import { ethers } from 'ethers'
import { useAtomValue, useSetAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import SmartButton from 'src/common/components/Buttons/SmartButton'
import { FULL_DATE_TIME_FORMAT } from 'src/common/consts/dateFormats'
import { DotcEnums } from 'src/common/contracts/enums/dotc'
import { getFeeAmount } from 'src/common/contracts/hooks/dotc/utils'
import { TransactionResult } from 'src/common/contracts/hooks/useExecuteTx'
import useSendTx from 'src/common/contracts/hooks/useSendTx'
import TokenDisplay from 'src/common/TokenDisplay'
import Icon from 'src/common/ui-kit/Icon/Icon'
import { displayAmount } from 'src/common/utils/formatters/number'
import {
  buyTokenAmountAtom,
  buyTokenAtom,
  limitOrderOptionsAtom,
  pricePerUnitAtom,
  sellTokenAmountAtom,
  sellTokenAtom,
  successfulCreatedOfferIdAtom,
  successfulTxHashAtom,
} from '../../stores/trade-store'
import TxStatusView from '../TxStatusView'
import useLimitOrder from './hooks/useLimitOrder'
import useSendBatchTxs from 'src/common/contracts/hooks/useSendBatchTxs'
import useConfig from 'src/config/hooks/useConfig'
import useAuthorizations from 'src/common/queries/useAuthorizations/useAuthorizations'
import { shortenAddress } from 'thirdweb/utils'

const ReviewLimit = () => {
  const { t } = useTranslation('trade', { keyPrefix: 'reviewLimit' })
  const navigate = useNavigate()
  const config = useConfig()

  const sellToken = useAtomValue(sellTokenAtom)
  const buyToken = useAtomValue(buyTokenAtom)

  const sellTokenAmount = useAtomValue(sellTokenAmountAtom)
  const buyTokenAmount = useAtomValue(buyTokenAmountAtom)
  const pricePerUnit = useAtomValue(pricePerUnitAtom)

  const limitOrderOptions = useAtomValue(limitOrderOptionsAtom)
  const setSuccessfulTxHash = useSetAtom(successfulTxHashAtom)
  const setSuccessfulCreatedOfferId = useSetAtom(successfulCreatedOfferIdAtom)

  const { getAuthorization } = useAuthorizations()

  const {
    gasEstimateInEther,
    refetchGasEstimate,
    refetchAllowance,
    needsApproval,
    approveTokenTx,
    loading: limitOrderLoading,
    makeOfferTx,
  } = useLimitOrder()

  const {
    sendTx: sendApproveTokenTx,
    isPending: isPendingApproveToken,
    status: statusApproveToken,
  } = useSendTx()
  const {
    sendBatchTxs: sendMakeOfferTx,
    isPending: isPendingMakeOffer,
    status: statusMakeOffer,
  } = useSendBatchTxs()

  const makeOfferLoaderStatus = isPendingMakeOffer
    ? 'loading'
    : statusMakeOffer === 'success'
    ? 'success'
    : 'pending'

  const approveTokenLoaderStatus = isPendingApproveToken
    ? 'loading'
    : !needsApproval || statusApproveToken === 'success'
    ? 'success'
    : 'pending'

  const loading =
    limitOrderLoading || isPendingApproveToken || isPendingMakeOffer

  const onSuccessMakeOffer = ({ hash, logs }: TransactionResult) => {
    const createdOfferTopics = logs?.find?.(
      log =>
        log.address.toLocaleLowerCase() ===
        config.contractsAddresses.dotcContractAddress.toLowerCase()
    )?.topics
    if (!createdOfferTopics || createdOfferTopics.length === 0) return

    const hexOfferId = createdOfferTopics.at(-1)
    if (hexOfferId) {
      const offerId = ethers.utils.hexStripZeros(hexOfferId)
      if (offerId) {
        setSuccessfulCreatedOfferId(offerId)
      }
    }

    setSuccessfulTxHash(hash)
    navigate('/trade/limit/success')
  }

  const handlePlaceOrder = async () => {
    const _makeOfferTx = makeOfferTx()
    if (!_makeOfferTx) return
    try {
      const txMakeOfferResult = await sendMakeOfferTx(_makeOfferTx)
      if (txMakeOfferResult)
        onSuccessMakeOffer({
          hash: txMakeOfferResult.transactionHash,
          logs: txMakeOfferResult.logs,
        })
    } catch (error) {
      console.error('Error placing order:', error)
    }
  }

  const handleBack = () => {
    navigate(-1)
  }

  const priceLabel = limitOrderOptions.dynamic
    ? t('price.dynamic')
    : t('price.fixed')

  const dynamicPriceValue =
    limitOrderOptions.percentage === 0
      ? t('market')
      : t('marketWithPercentage', {
          percentage: limitOrderOptions.percentage,
          operator:
            limitOrderOptions.percentageType === DotcEnums.PercentageType.Plus
              ? '+'
              : '-',
        })

  const fixedPriceValue = `${displayAmount(pricePerUnit)} ${buyToken?.symbol}/${
    sellToken?.symbol
  }`

  const priceValue = limitOrderOptions.dynamic
    ? dynamicPriceValue
    : fixedPriceValue

  const allQualifiers = [
    ...limitOrderOptions.authorizationAddresses,
    ...(limitOrderOptions.otherAuthorizationAddress
      ? [limitOrderOptions.otherAuthorizationAddress]
      : []),
  ]

  const qualifierValue =
    allQualifiers.length > 0
      ? allQualifiers
          .map(address => {
            if (!address) return

            if (!ethers.utils.isAddress(address)) return address

            const auth = getAuthorization(address)

            return auth ? auth.name : shortenAddress(address)
          })
          .join(', ')
      : 'No'

  const offerFields = [
    [priceLabel, priceValue],
    [
      t('expiry'),
      dayjs
        .unix(limitOrderOptions.expiryTimestamp)
        .format(FULL_DATE_TIME_FORMAT),
    ],
    limitOrderOptions.timelockPeriod
      ? [t('timelock'), limitOrderOptions.timelockPeriod.toString() + ' days']
      : [],
    [
      t('partialOrders'),
      limitOrderOptions.takingOfferType ===
      DotcEnums.TakingOfferType.PartialOffer
        ? 'Yes'
        : 'No',
    ],
    // [t('qualifier'), qualifierValue],
    [
      t('makerFee'),
      `${displayAmount(getFeeAmount(buyTokenAmount))} ${buyToken?.symbol}`,
    ],
  ]

  return (
    <>
      <div className='paper p-4 mb-4'>
        <div className='flex items-center mb-4'>
          <Icon
            name='arrow-right'
            className='mr-4 rotate-180 cursor-pointer'
            onClick={handleBack}
          />
          <div className='typo-title2 text-main-accent'>
            {t('reviewLimitOrder')}
          </div>
        </div>
        <div className='flex flex-col gap-2'>
          <TokenDisplay
            readonly
            className='mb-2'
            token={sellToken}
            amount={sellTokenAmount}
            label='Sell'
          />
          <TokenDisplay
            readonly
            token={buyToken}
            amount={buyTokenAmount}
            label='Buy'
          />
        </div>
        <hr className='my-4' />
        <div className='flex flex-col gap-2'>
          {offerFields.map(
            ([text, value]) =>
              text && (
                <div key={text} className='flex justify-between'>
                  <div className='typo-label1'>{text}</div>
                  <div className='typo-label1 text-main-accent'>
                    {value || <div className='loader w-3 h-3' />}
                  </div>
                </div>
              )
          )}
        </div>
        <hr className='my-4' />
        <TxStatusView
          approveSymbol={sellToken?.symbol}
          approveStatus={approveTokenLoaderStatus}
          mainStatus={makeOfferLoaderStatus}
        />
      </div>
      <SmartButton
        onClick={handlePlaceOrder}
        loading={loading}
        className='w-full'
      >
        {t('placeOrder')}
      </SmartButton>
    </>
  )
}

export default ReviewLimit
